import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * useSessionTimeout
 * 
 * Dos funciones:
 * 1. Cierra la sesión después de 20 minutos de inactividad.
 * 2. Cierra la sesión al cerrar la pestaña/ventana del navegador.
 * 
 * Uso: llamar una vez en un componente que esté siempre montado (Navbar, App, etc.)
 *   useSessionTimeout();
 */

const TIMEOUT_MS = 20 * 60 * 1000; // 20 minutos en milisegundos
const SESSION_FLAG = 'bacmt_session_active';

export default function useSessionTimeout() {
    const navigate = useNavigate();
    const timerRef = useRef(null);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('usuario');
        sessionStorage.removeItem(SESSION_FLAG);
        navigate('/login');
    }, [navigate]);

    // --- 1. Timeout por inactividad (20 min) ---
    const resetTimer = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            alert('Tu sesión expiró por inactividad. Ingresá de nuevo.');
            logout();
        }, TIMEOUT_MS);
    }, [logout]);

    useEffect(() => {
        // Solo activo si hay token (está logueado)
        const token = localStorage.getItem('token');
        if (!token) return;

        // Eventos que indican actividad del usuario
        const eventos = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
        eventos.forEach(e => document.addEventListener(e, resetTimer, { passive: true }));
        resetTimer(); // arrancar el timer

        return () => {
            eventos.forEach(e => document.removeEventListener(e, resetTimer));
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [resetTimer]);

    // --- 2. Cerrar sesión al cerrar ventana/pestaña ---
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        // Al cargar la app, marcamos que hay una sesión activa en esta pestaña
        sessionStorage.setItem(SESSION_FLAG, 'true');

        // beforeunload: se dispara al cerrar pestaña/ventana Y al refrescar.
        // Para distinguir cierre de refresco: usamos un flag en sessionStorage.
        // sessionStorage se borra al cerrar pestaña, pero persiste al refrescar.
        // Entonces: al cargar, si no hay flag en sessionStorage pero sí token en
        // localStorage → el navegador se cerró y reabrió → limpiamos el token.
    }, []);

    // Al cargar la app: si no hay flag de sesión pero sí token, es un reopen → logout
    useEffect(() => {
        const token = localStorage.getItem('token');
        const sesionActiva = sessionStorage.getItem(SESSION_FLAG);

        if (token && !sesionActiva) {
            // Había token pero no flag de sesión → el navegador se cerró y reabrió
            localStorage.removeItem('token');
            localStorage.removeItem('usuario');
            navigate('/login');
        }
    }, [navigate]);
}